/** A fold that lands this far short of the limit hides more than it saves
 *  cutting cleanly, so below it the fold cuts at the limit instead. */
const MIN_SNAP_RATIO = 0.5;

/**
 * Where a folded answer stops, in px from its top: the lowest block edge at or
 * under `limit`, so the fold falls between blocks rather than through the
 * middle of a card. Falls back to `limit` when no edge is close enough to it.
 */
export function snapFoldHeight(
  edges: readonly number[],
  limit: number
): number {
  const fits = edges.filter((edge) => edge <= limit);
  const best = fits.length > 0 ? Math.max(...fits) : 0;
  return best >= limit * MIN_SNAP_RATIO ? best : limit;
}

/**
 * The bottom edges, in px from the top of `root`, of the blocks a fold at
 * `limit` could fall between. A block that ends above the limit counts whole
 * and is not looked inside; one that straddles the limit is looked inside for
 * smaller blocks. So a card shorter than the limit is never cut, and a taller
 * one is cut between its rows.
 */
export function blockEdges(root: Element, limit: number): number[] {
  const rootTop = root.getBoundingClientRect().top;
  const edges: number[] = [];
  const visit = (el: Element) => {
    for (const child of Array.from(el.children)) {
      const rect = child.getBoundingClientRect();
      const top = rect.top - rootTop;
      const bottom = rect.bottom - rootTop;
      if (top >= limit || bottom <= top) continue;
      if (bottom <= limit) {
        edges.push(bottom);
      } else {
        visit(child);
      }
    }
  };
  visit(root);
  return edges;
}
